"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, forwardRef } from "react";
import { Icon } from "@/components/formatters/MXPIcon";
import { TerraLink } from "../TerraLink/TerraLink";

/** Mirrors the `bannerType` validation in migrations/mxp/33.0.js. */
export type BannerType = "Alert" | "Operational";

/** A `simpleLink` entry after the formatter layer has mapped it. */
export interface AlertBannerLink {
  label?: string | null;
  destination?: string | null;
  ariaLabel?: string | null;
}
export interface AlertBannerProps {
  /** `message` — desktop / full-width banner sentence. */
  message?: string | null;
  /** `mobileMessage` — optional shorter copy shown on phones (below 768px). */
  mobileMessage?: string | null;
  /** `bannerType` — drives both placement and color. */
  bannerType?: string | null;
  /** `link` — single inline link rendered with a right chevron. */
  link?: AlertBannerLink | null;
  /** `isDismissible` — renders the close (X) button. */
  isDismissible?: boolean | null;
  /** `isActive` — master on/off toggle. Anything but an explicit `false` is on. */
  isActive?: boolean | null;
  /** Called after the close button hides the banner, for session persistence. */
  onDismiss?: () => void;
}

/**
 * The Alert variant is fixed above the site header, so the layout has to offset
 * the header and page content by this much. Keep in sync with the
 * `min-h-[55px]` below — Tailwind only scans literal class strings.
 */
export const ALERT_BANNER_HEIGHT = 55;

// `.site-navigation` is `position: fixed; z-index: 9`, so the Alert bar needs
// to clear it to sit above the header.
const VARIANT_CLASSES: Record<BannerType, string> = {
  Alert: "fixed top-0 right-0 left-0 z-[10] bg-yellow-m10",
  Operational: "relative bg-blue-sky"
};
export const AlertBanner = forwardRef<HTMLDivElement, AlertBannerProps>(function AlertBanner(t0, ref) {
  const $ = _c(28);
  const {
    message,
    mobileMessage,
    bannerType,
    link,
    isDismissible: t1,
    isActive: t2,
    onDismiss
  } = t0;
  const isDismissible = t1 === undefined ? false : t1;
  const isActive = t2 === undefined ? true : t2;
  const [isDismissed, setIsDismissed] = useState(false);
  const variant = bannerType === "Alert" ? "Alert" : "Operational";
  const hasLink = Boolean(link?.label && link?.destination);
  const hasMobileMessage = Boolean(mobileMessage);
  if (isActive === false || isDismissed) {
    return null;
  }
  if (!message && !mobileMessage && !hasLink) {
    return null;
  }
  let t3;
  if ($[0] !== onDismiss) {
    t3 = () => {
      setIsDismissed(true);
      onDismiss?.();
    };
    $[0] = onDismiss;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleDismiss = t3;
  const t4 = variant === "Alert" ? "Site alert" : "Site notice";
  let t5;
  if ($[2] !== variant) {
    t5 = variant.toLowerCase();
    $[2] = variant;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = `t2-alert-banner--${t5}`;
  const t7 = VARIANT_CLASSES[variant];
  let t8;
  if ($[4] !== t6 || $[5] !== t7) {
    t8 = ["t2-alert-banner w-full text-gray-m10", t6, t7];
    $[4] = t6;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = t8.join(" ");
  let t10;
  if ($[7] !== hasMobileMessage || $[8] !== message || $[9] !== mobileMessage) {
    t10 = hasMobileMessage ? <><p className="m-0 text-base leading-6 md:hidden">{mobileMessage}</p>{message ? <p className="m-0 hidden text-base leading-6 md:block">{message}</p> : null}</> : message ? <p className="m-0 text-base leading-6">{message}</p> : null;
    $[7] = hasMobileMessage;
    $[8] = message;
    $[9] = mobileMessage;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== hasLink || $[12] !== link?.ariaLabel || $[13] !== link?.destination || $[14] !== link?.label) {
    t11 = hasLink ? <TerraLink linkType="text" isIcon={true} label={link?.label ?? undefined} destination={link?.destination ?? undefined} ariaLabel={link?.ariaLabel ?? undefined} className="items-center gap-1 font-bold text-gray-m10 [text-decoration:none]!" /> : null;
    $[11] = hasLink;
    $[12] = link?.ariaLabel;
    $[13] = link?.destination;
    $[14] = link?.label;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== handleDismiss || $[17] !== isDismissible) {
    t12 = isDismissible ? <button type="button" onClick={handleDismiss} aria-label="Dismiss notice" className="absolute top-1/2 right-4 flex h-[34px] w-[34px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-white p-0 shadow-[0_-1px_5px_rgba(0,0,0,0.1)] [&_.t2-icon]:pointer-events-none [&_.t2-icon_svg_path]:fill-[#252a2e]"><Icon type="t2u-close" size={14} className="text-gray-m10" /></button> : null;
    $[16] = handleDismiss;
    $[17] = isDismissible;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t12) {
    t13 = <div className="relative mx-auto flex min-h-[55px] max-w-[1445px] flex-wrap items-center justify-start gap-x-6 gap-y-1 px-5 py-2 pr-14 text-left md:justify-center md:px-16 md:pr-16 md:text-center">{t10}{t11}{t12}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== ref || $[24] !== t13 || $[25] !== t4 || $[26] !== t9) {
    t14 = <div ref={ref} role="region" aria-label={t4} className={t9}>{t13}</div>;
    $[23] = ref;
    $[24] = t13;
    $[25] = t4;
    $[26] = t9;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
});